/**
 * Get the value of a query parameter from the URL
 * @param paramName - The name of the query parameter
 * @param defaultValue - The default value to return if the query parameter is not found
 * @returns The value of the query parameter or the default value if the query parameter is not found
 */
export default function getUrlQueryParamValue<T>(
  paramName: string,
  defaultValue?: T,
): T | undefined {
  const urlParams = new URLSearchParams(window.location.search);
  const value = urlParams.get(paramName);

  // If no value found, return defaultValue
  if (value === null) {
    return defaultValue;
  }

  // If defaultValue is a string, return the raw value as string
  if (typeof defaultValue === 'string') {
    return value as T;
  }

  // If defaultValue is a number, try to parse as number
  if (typeof defaultValue === 'number') {
    const parsed = Number(value);

    return (isNaN(parsed) ? defaultValue : parsed) as T;
  }

  // If defaultValue is a boolean, parse boolean strings
  if (typeof defaultValue === 'boolean') {
    const lowerValue = value.toLowerCase().trim();

    if (lowerValue === 'true' || lowerValue === '1') return true as T;

    if (lowerValue === 'false' || lowerValue === '0') return false as T;

    return defaultValue;
  }

  // For objects/arrays, try JSON parsing
  try {
    return JSON.parse(value) as T;
  } catch (error) {
    // If no defaultValue provided, return raw string (most common use case)
    return value as T;
  }
}
